import { Formik, Form } from 'formik'
import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { format } from 'date-fns'
import { useDispatch } from 'react-redux'
import { Lock } from '@phosphor-icons/react'
import { useSelector } from 'react-redux'

import FormSteps from '../../../FormSteps/FormSteps'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import useUnsavedChanges from '../../../../helpers/hooks/useUnsavedChanges'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import API from '../../../../api/api'
import { isFile, getFullDateFromDayMinutes } from '../../../../helpers/helpers'
import AffectedReservationsPopup from '../../AffectedReservationsPopup/AffectedReservationsPopup'
import { csvReservationsFields } from '../../../../helpers/constants'
import csvDownload from '../../../../helpers/csvDownload'
import {
  cancelAmenityReservations,
  editAmenity,
  upsertAmenity,
} from '../../../../reducers/Organisation/reservationsReducer'
import { selectUserData } from '../../../../reducers/userDataReducer'

import {
  getAmenityFormValues,
  makeValidationSingleReservation,
  makeValidationMultidayReservation,
  makeStepValidationSchemas,
  preparePayload,
} from './helpers'
import AmenityDetails from './Steps/AmenityDetails'
import PricingAndAvailability from './Steps/PricingAndAvailability'
import ReservationLimits from './Steps/ReservationLimits'
import AmenityMarketplace from './Steps/AmenityMarketplace'
import Spaces from './Steps/Spaces'
import FormNavButtons from './components/FormNavButtons'
import { UserRoleEnum } from '@/models/features/user.models'

const STEP_KEYS = [
  'details',
  'pricingAvailability',
  'reservationLimits',
  'amenityMarketplace',
  'spaces',
]

const CANNOT_BE_CHANGED_STATUS = 409

const EditAmenityForm = () => {
  const { t } = useTranslation(['reservations', 'common'])
  const stepValidationSchemas = makeStepValidationSchemas(t)
  const stepLabels = {
    details: t('reservations:amenityForms.stepsNav.details'),
    pricingAvailability: t('reservations:amenityForms.stepsNav.pricingAvailability'),
    reservationLimits: t('reservations:amenityForms.stepsNav.reservationLimits'),
    amenityMarketplace: t('reservations:amenityForms.stepsNav.amenityMarketplace'),
    spaces: t('reservations:amenityForms.stepsNav.spaces'),
  }
  const [selectedBuilding, setSelectedBuilding] = useState(null)
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [amenity, setAmenity] = useState(null)
  const [confirmOpened, setConfirmOpened] = useState(false)
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [affectedReservations, setAffectedReservations] = useState(null)
  const [isTimeIntervalChanged, setIsTimeIntervalChanged] = useState(false)
  const [canUpdateWithoutCancellingReservations, setCanUpdateWithoutCancellingReservations] =
    useState(false)
  const [resetKey, setResetKey] = useState(0)
  const { displaySnackbar } = useSnackbar()
  const { amenityId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const nextButtonRef = useRef(null)
  const shouldClickNextRef = useRef(false)

  const cancelAmenityReservationsAction = (...args) => dispatch(cancelAmenityReservations(...args))
  const editAmenityAction = (...args) => dispatch(editAmenity(...args))

  const userRole = useSelector(selectUserData)?.user.role
  const shouldRedirect = ![
    UserRoleEnum.enum.Administrator,
    UserRoleEnum.enum.Manager,
    UserRoleEnum.enum.PropertyManager,
  ].includes(userRole)

  const spacesIndicator = selectedBuilding?.amenityLocationAvailable ? (
    <span className="step__indicator step__indicator--new">
      {t('reservations:amenityForms.newIndicator')}
    </span>
  ) : (
    <span className="step__indicator step__indicator--lock">
      <Lock size={14} weight="bold" />
    </span>
  )

  const filteredSteps = STEP_KEYS.filter((stepKey) => {
    if (stepKey === 'amenityMarketplace' && !selectedBuilding?.amenityMarketplaceAvailable) {
      return false
    }
    return true
  }).map((stepKey) =>
    stepKey === 'spaces'
      ? { label: stepLabels[stepKey], indicator: spacesIndicator }
      : stepLabels[stepKey],
  )

  // Use the reusable hook for unsaved changes handling
  const { clearUnsavedChanges, handleStayOnPage, handleLeaveWithoutSaving, showLeaveConfirmation } =
    useUnsavedChanges({
      hasUnsavedChanges,
      onLeave: () => {
        setHasUnsavedChanges(false)
      },
    })

  const validateStep = async (validateForm, values) => {
    const stepSchema = stepValidationSchemas[currentStep]
    if (stepSchema) {
      try {
        await stepSchema.validate(values, { abortEarly: false })
        return true
      } catch (error) {
        console.error('Step validation failed:', error)
        // Trigger form validation to show errors
        validateForm()
        displaySnackbar(t('reservations:amenityForms.completeRequiredFields'), true)
        return false
      }
    }

    return true
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleDiscardChanges = (resetForm) => {
    resetForm()
    clearUnsavedChanges()
    setHasUnsavedChanges(false)
    setResetKey((prev) => prev + 1)
  }

  const handleSubmit = async (validateForm, values, { resetForm }) => {
    const isValid = await validateStep(validateForm, values)
    if (!isValid) {
      return
    }

    setIsSubmitting(true)
    try {
      const data = preparePayload(values, selectedBuilding.timezone)
      const { image, regulation, agreement, ...rest } = data
      const filesPayload = {}

      if (isFile(image)) {
        filesPayload.image = image
      }
      if (isFile(regulation)) {
        filesPayload.regulation = regulation
      }
      if (isFile(agreement)) {
        filesPayload.agreement = agreement
      }

      if (Object.keys(filesPayload).length > 0) {
        const { data: attachments = [] } = await API.uploadAmenityAttachments(filesPayload)
        if (attachments.length > 0) {
          for (const attachment of attachments) {
            rest[attachment.field] = attachment.name
          }
        }
      }

      // Persist explicit removal of a previously-saved Overview/Agreement file. Unchanged
      // files are intentionally omitted from the payload, so only send a clear when the
      // amenity had a file and it was removed in the form.
      if (!regulation && amenity?.regulation) {
        rest.regulation = null
      }
      if (!agreement && amenity?.agreement) {
        rest.agreement = null
      }

      const response = await editAmenityAction(
        {
          ...rest,
          buildingId: selectedBuilding.id,
          allowUpdate: canUpdateWithoutCancellingReservations,
        },
        amenityId,
      )
      clearUnsavedChanges()
      setHasUnsavedChanges(false)
      setAmenity((prev) => ({ ...prev, ...response }))
      resetForm()
      displaySnackbar(t('reservations:amenityForms.amenityUpdatedSuccessfully'))
    } catch (error) {
      if (error?.response?.data?.errCode === CANNOT_BE_CHANGED_STATUS) {
        const message = JSON.parse(error?.response?.data?.message || '{}')
        if (Object.keys(message).length > 0) {
          setAffectedReservations(message?.affectedReservations)
          setIsTimeIntervalChanged(message?.blockUpdateIfTimeIntervalChanged)
          return
        }
      }
      console.error('Error submitting form:', error)
      const message =
        error?.response?.data?.message || t('reservations:amenityForms.errorUpdatingAmenity')
      displaySnackbar(message, true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const cancelReservations = async () => {
    try {
      const reservations = affectedReservations.map((reservation) => reservation.id)
      await cancelAmenityReservationsAction(reservations, false, amenityId)
      nextButtonRef.current?.click()
    } catch ({ message }) {
      displaySnackbar(message || t('reservations:amenityForms.errorCancelingReservations'), true)
    } finally {
      setConfirmOpened(false)
      setAffectedReservations(null)
    }
  }

  const onProceedWithoutCancelling = async () => {
    shouldClickNextRef.current = true
    setCanUpdateWithoutCancellingReservations(true)
  }

  const downloadAffectedReservations = () => {
    const data = affectedReservations.map((reservation) => ({
      date: format(Number(reservation.date), 'MM/dd/yy'),
      time: format(getFullDateFromDayMinutes(reservation.startTime), 'h:mma'),
      amenity: reservation.amenity.name,
      duration: reservation.duration + 'min',
      price: reservation.chargeAmount || '-',
      firstName: reservation.client.firstName,
      lastName: reservation.client.lastName,
      unit: reservation.client.unit,
      phone: reservation.client.phone,
      email: reservation.client.email,
    }))

    csvDownload(
      csvReservationsFields,
      data,
      'Affected-reservations.csv',
      t('reservations:amenityForms.affectedReservationsCsvTitle'),
    )
  }

  const handleDeleteAmenity = async () => {
    try {
      await API.deleteAmenity(amenityId)
      clearUnsavedChanges()
      setHasUnsavedChanges(false)
      displaySnackbar(t('reservations:amenityForms.amenityDeletedSuccessfully'))
      navigate('/dashboard/building-dashboard/reservations/amenities')
    } catch (error) {
      console.error('Error deleting amenity:', error)
      const message = error?.message || t('reservations:amenityForms.errorDeletingAmenity')
      displaySnackbar(message, true)
    } finally {
      setDeleteConfirmOpened(false)
    }
  }

  // Get the appropriate validation schema based on amenity type
  const getValidationSchema = () => {
    return amenity.multiDayBooking
      ? makeValidationMultidayReservation(t)
      : makeValidationSingleReservation(t)
  }

  useEffect(() => {
    const fetchAmenity = async () => {
      const response = await API.getAmenityById(amenityId)
      setAmenity(response?.data)
      setSelectedBuilding(response?.data?.buildingId)
      if (response?.data) {
        dispatch(upsertAmenity(response.data))
      }
    }
    fetchAmenity()
  }, [amenityId, dispatch])

  useEffect(() => {
    if (shouldClickNextRef.current) {
      nextButtonRef.current?.click()
      shouldClickNextRef.current = false
      setAffectedReservations(null)
    }
  }, [canUpdateWithoutCancellingReservations])

  useEffect(() => {
    window.deleteAmenityHandler = () => setDeleteConfirmOpened(true)
    return () => {
      delete window.deleteAmenityHandler
    }
  }, [])

  if (shouldRedirect) {
    return <Navigate replace to="/not-found" />
  }

  return (
    <div className={`reservations-amenities-form ${!amenity ? 'is-loading' : ''}`}>
      <Formik
        enableReinitialize
        initialValues={getAmenityFormValues(amenity, selectedBuilding?.timezone)}
        validationSchema={getValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ setFieldValue, handleChange, values, validateForm, errors, dirty, resetForm }) => {
          // Track form changes
          if (dirty && !hasUnsavedChanges) {
            setHasUnsavedChanges(true)
          }

          return (
            <>
              <FormSteps
                numbered={false}
                steps={filteredSteps}
                currentStep={currentStep}
                onStepClick={setCurrentStep}
              />
              <Form>
                {currentStep === 0 && (
                  <AmenityDetails
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    errors={errors}
                    isEdit
                  />
                )}
                {currentStep === 1 && (
                  <PricingAndAvailability
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    errors={errors}
                    resetKey={resetKey}
                  />
                )}
                {currentStep === 2 && (
                  <ReservationLimits
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    errors={errors}
                  />
                )}
              </Form>
              {currentStep === 3 && selectedBuilding?.amenityMarketplaceAvailable && (
                <AmenityMarketplace amenity={values} />
              )}
              {currentStep === filteredSteps.length - 1 && <Spaces amenity={amenity} />}
              <FormNavButtons
                currentStep={currentStep}
                totalSteps={filteredSteps.length}
                onBack={handleBack}
                onSubmit={() => handleSubmit(validateForm, values, { resetForm })}
                onDiscardChanges={() => handleDiscardChanges(resetForm)}
                isSubmitting={isSubmitting}
                hasUnsavedChanges={hasUnsavedChanges}
                display={currentStep !== 3 && currentStep !== filteredSteps.length - 1}
                isEdit
                nextButtonRef={nextButtonRef}
              />
            </>
          )
        }}
      </Formik>

      <ConfirmPopup
        isOpen={showLeaveConfirmation}
        cancelCallback={handleStayOnPage}
        confirmCallback={handleLeaveWithoutSaving}
        title={t('reservations:amenityForms.leaveWithoutSaving.title')}
        body={t('reservations:amenityForms.leaveWithoutSaving.body')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('reservations:amenityForms.leaveWithoutSaving.leaveAnyway')}
      />
      <ConfirmPopup
        title={t('reservations:amenityForms.confirmCancelAffectedReservations')}
        cancelText={t('common:actions.close')}
        confirmText={t('reservations:amenityForms.accept')}
        isOpen={confirmOpened}
        cancelCallback={() => setConfirmOpened(false)}
        confirmCallback={cancelReservations}
      />
      <ConfirmPopup
        title={t('reservations:amenityForms.deleteAmenity.title')}
        body={t('reservations:amenityForms.deleteAmenity.body')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.delete')}
        isOpen={deleteConfirmOpened}
        cancelCallback={() => setDeleteConfirmOpened(false)}
        confirmCallback={handleDeleteAmenity}
      />
      <AffectedReservationsPopup
        isOpen={affectedReservations !== null && !confirmOpened}
        proceedWithoutCancelling={isTimeIntervalChanged}
        onCancel={() => setAffectedReservations(null)}
        onDownloadReservations={downloadAffectedReservations}
        onCancelReservations={() => setConfirmOpened(true)}
        onProceedWithoutCancelling={onProceedWithoutCancelling}
      />
    </div>
  )
}

export default EditAmenityForm
